import dayjs, { type Dayjs } from "dayjs"

import {
  type ContractFeeType,
  type Contract,
  type SimpleAmount,
  type ChildPresences,
  FactorCalculationBase,
  BookingTypeEnum,
  BookingTimeCategory,
  type InstitutionFilter,
  type BookingTimeRelatedAmount,
  BookingTimeCalculationBase,
  type Booking,
  type FeeType,
  FeeCategory,
  BookingDaysEnum,
} from "@/graphql/types"

import { determineTimeCategory, extractBookingDaysAndPolicy } from "./bookings"
import { isVacationBooking } from "./composables/bookingCalendar"
import { isDateRangeOverlap, isMonthInRange } from "./date"
import { splitLongId } from "./entity"
import { fetchMonthOfPresences } from "./presences"

export async function calculateFee(
  childId: string,
  contract: Contract | undefined,
  month: Dayjs,
  contractFeeType: ContractFeeType,
  context: InstitutionFilter
) {
  let factor = null
  let singleValue = null
  const bookingTimeCategory = null
  const defaultReturn = { factor, singleValue, bookingTimeCategory }

  const isValidMonth = isMonthInRange(
    month,
    contractFeeType.startDate,
    contractFeeType.endDate ?? contract?.endOfContract
  )
  if (!isValidMonth) return defaultReturn

  const feeType = contractFeeType?.feeType
  const isFeeTypeActive = feeType?.active
  if (!isFeeTypeActive) return defaultReturn

  const activeContributionRate = getValidTimeRangeItem(feeType.contributionRates ?? [], month)
  if (!activeContributionRate) return defaultReturn

  const { factorCalculationBase, amountBased, bookingTimeCalculationBase, amount } =
    activeContributionRate
  const simpleAmount = getFeeAmount(amount)
  const isStatic = !factorCalculationBase && !amountBased && !bookingTimeCalculationBase

  singleValue = simpleAmount

  if (isStatic) {
    return {
      factor: 1,
      singleValue,
      bookingTimeCategory,
    }
  }

  if (bookingTimeCalculationBase) {
    const [singleValue, bookingTimeCategory] = getValueFromBookings(
      bookingTimeCalculationBase,
      contract,
      month,
      amount as BookingTimeRelatedAmount,
      feeType
    )

    return {
      factor: singleValue && 1,
      singleValue,
      bookingTimeCategory,
    }
  }

  if (factorCalculationBase) {
    factor = await calculateFactorFromBase(
      factorCalculationBase,
      month,
      childId,
      context,
      contractFeeType.quantity ?? null
    )
  }

  if (amountBased) {
    if (!factorCalculationBase) factor = 1
    singleValue = contractFeeType.price ?? singleValue
  }

  return { factor, singleValue, bookingTimeCategory: null }
}

async function calculateFactorFromBase(
  base: FactorCalculationBase,
  month: Dayjs,
  childId: string,
  context: InstitutionFilter,
  defaultFactor: number | null
) {
  let presences: ChildPresences[] = []

  const { current, previous } = getRequiredPresenceMonths(base)

  if (current) {
    presences = await fetchMonthOfPresences(context, month)
  }

  if (previous) {
    const prev = month.subtract(1, "month")
    presences = await fetchMonthOfPresences(context, prev)
  }

  const groupedPresences = groupChildPresenceKindCountsByMonth(presences, childId)

  return calculateFeeFactorFromPresences(base, month, groupedPresences, defaultFactor)
}

export function getValidTimeRangeItem<T extends { from: string; to?: string | null }>(
  items: T[],
  month: Dayjs
): T | undefined {
  const firstDayOfMonth = month.startOf("month")
  const lastDayOfMonth = month.endOf("month")

  return items
    .filter((item) =>
      isDateRangeOverlap(item.from, item.to ?? null, firstDayOfMonth, lastDayOfMonth)
    )
    .sort((a, b) => dayjs(b.from).valueOf() - dayjs(a.from).valueOf())[0]
}

export function groupChildPresenceKindCountsByMonth(
  presencesList: ChildPresences[],
  childId: string
): Record<string, Record<string, number>> {
  if (!presencesList.length) return {}

  const flatPresences = presencesList
    .flatMap((entry) => entry.presences)
    .filter((presence) => presence.childId === splitLongId(childId)[3])

  return flatPresences.reduce<Record<string, Record<string, number>>>((acc, presence) => {
    const monthKey = dayjs(presence.date).format("YYYY-MM")
    const kind = presence.kind

    if (!acc[monthKey]) acc[monthKey] = {}
    acc[monthKey][kind] = (acc[monthKey][kind] || 0) + 1

    return acc
  }, {})
}

function calculateFeeFactorFromPresences(
  factorCalculationBase: FactorCalculationBase,
  month: Dayjs,
  presences: Record<string, Record<string, number>>,
  defaultFactor: number | null
): number | null {
  const monthKey = month.format("YYYY-MM")
  const prevMonthKey = month.subtract(1, "month").format("YYYY-MM")

  const current = presences?.[monthKey] || {}
  const previous = presences?.[prevMonthKey] || {}

  switch (factorCalculationBase) {
    case FactorCalculationBase.PresenceDaysInCurrentMonth:
      return (current?.["PRESENT"] || 0) + (current?.["PRESENT_WITH_MEAL"] || 0) || null

    case FactorCalculationBase.PresenceDaysInPreviousMonth:
      return (previous?.["PRESENT"] || 0) + (previous?.["PRESENT_WITH_MEAL"] || 0) || null

    case FactorCalculationBase.ExcusedAbsenceDaysInCurrentMonth:
      return current?.["ABSENT_AND_EXCUSED"] || null

    case FactorCalculationBase.ExcusedAbsenceDaysInPreviousMonth:
      return previous?.["ABSENT_AND_EXCUSED"] || null

    case FactorCalculationBase.Manual:
      return defaultFactor
    default:
      return null
  }
}

export function getRequiredPresenceMonths(factorBase: FactorCalculationBase) {
  return {
    current: [
      FactorCalculationBase.PresenceDaysInCurrentMonth,
      FactorCalculationBase.ExcusedAbsenceDaysInCurrentMonth,
    ].includes(factorBase),

    previous: [
      FactorCalculationBase.PresenceDaysInPreviousMonth,
      FactorCalculationBase.ExcusedAbsenceDaysInPreviousMonth,
    ].includes(factorBase),
  }
}

function getFeeAmount(
  amount: SimpleAmount | BookingTimeRelatedAmount,
  avrgTimeCategory?: string
): number | null {
  if (amount.__typename === "SimpleAmount") {
    return amount?.amount ?? null
  }

  if (amount.__typename === "BookingTimeRelatedAmount" && avrgTimeCategory) {
    const key = avrgTimeCategory.toLowerCase()
    return key in amount
      ? ((amount[key as keyof BookingTimeRelatedAmount] as number) ?? null)
      : null
  }

  return null
}

export function calculateAvrgTimeCategoryForStandardBooking(
  contract: Contract | undefined,
  month: Dayjs
): BookingTimeCategory | null {
  const lastDayOfAccountingYear = month.endOf("year")

  // 1. Get the valid standard booking
  const standardBooking = getValidTimeRangeItem(
    getBookings(contract, "standard", lastDayOfAccountingYear),
    month
  )
  if (!standardBooking) return null

  const standardCategory = standardBooking.timeFactor?.category
  const standardFactor = bookingTimeCategoryToFeeTimeFactor(standardCategory)
  if (standardFactor == null) return null

  // 2. Get valid alternative bookings for the month
  const validAlternativeBookings = getValidAlternativeBookings(
    contract,
    month,
    lastDayOfAccountingYear
  )
  if (!validAlternativeBookings.length) return standardCategory

  // Skip if all alternative bookings share the same category with standard booking
  const hasDifferentCategory = validAlternativeBookings.some(
    (b) => b.timeFactor?.category !== standardCategory
  )
  if (!hasDifferentCategory) return standardCategory

  // 4. Calculate averageDailyHours and determine time category
  const averageTimeCategory = calculateAverageTimeCategory(
    standardBooking.timeFactor?.hoursPerWeek ?? 0,
    validAlternativeBookings
  )

  return averageTimeCategory
}

export function calculateAverageTimeCategory(
  standardWeeklyHours: number,
  alternativeBookings: Booking[]
): BookingTimeCategory {
  const averageDailyHours = calculateAverageDailyHours(standardWeeklyHours, alternativeBookings)
  return determineTimeCategory(averageDailyHours)
}

export function getValidAlternativeBookings(
  contract: Contract | undefined,
  month: Dayjs,
  lastDayOfAccountingYear: Dayjs
) {
  const alternativeBookings = getBookings(contract, "alternative", lastDayOfAccountingYear)
  return alternativeBookings.filter((b) =>
    month.endOf("month").isBetween(dayjs(b.from), dayjs(b.to), "date", "[]")
  )
}

export function getSumAltPercent(alternativeBookings: Booking[]) {
  return alternativeBookings.reduce((sum, b) => {
    const percent = getAlternativePercent(b)
    return sum + percent / 100
  }, 0)
}

/** Calculates average time factor*/
export function calculateAverageDailyHours(
  standardBookingHours: number,
  alternativeBookings: Booking[]
): number {
  const sumAltPercent = getSumAltPercent(alternativeBookings)

  const weightedAlternativeHours = alternativeBookings.reduce((sum, a) => {
    const altHours = a?.timeFactor?.hoursPerWeek ?? 0
    const percent = getAlternativePercent(a) / 100
    return sum + altHours * percent
  }, 0)

  const averageWeeklyHours = standardBookingHours * (1 - sumAltPercent) + weightedAlternativeHours

  const WORKING_DAYS_PER_WEEK = 5
  const averageDailyHours = averageWeeklyHours / WORKING_DAYS_PER_WEEK

  return averageDailyHours
}

function getAlternativePercent(b: Booking): number {
  return b.typeObject && "percent" in b.typeObject ? (b.typeObject.percent ?? 0) : 0
}

function getValueFromBookings(
  bookingTimeCalculationBase: BookingTimeCalculationBase,
  contract: Contract | undefined,
  month: Dayjs,
  amount: BookingTimeRelatedAmount,
  feeType: FeeType
): [number | null, BookingTimeCategory | null] {
  //NOTE: This could be different for every inst., so adjust based on inst. preference for Betreuung Jahr when possible
  const firstDayAccountingOfYear = month.startOf("year")
  const lastDayOfAccountingYearOfYear = month.endOf("year")

  const avrgTimeCategory = calculateAvrgTimeCategoryForStandardBooking(contract, month)

  switch (bookingTimeCalculationBase) {
    case BookingTimeCalculationBase.OnlyStandardBookings:
      return [
        getValueFromStandardAndVacationBookings(
          amount,
          contract,
          month,
          "standard",
          lastDayOfAccountingYearOfYear,
          avrgTimeCategory
        ),
        avrgTimeCategory,
      ]
    case BookingTimeCalculationBase.OnlyVacationBookings:
      return [
        getValueFromStandardAndVacationBookings(
          amount,
          contract,
          month,
          "vacation",
          lastDayOfAccountingYearOfYear
        ),
        avrgTimeCategory,
      ]
    case BookingTimeCalculationBase.StandardBookingsAndVacationBookingsYearEnd:
      return [
        getValueFromYearEnd(
          amount,
          contract,
          month,
          feeType,
          firstDayAccountingOfYear,
          lastDayOfAccountingYearOfYear,
          avrgTimeCategory
        ),
        avrgTimeCategory,
      ]
    case BookingTimeCalculationBase.DistributeDifferenceOfVacationAndStandardBooking: {
      return [
        getValueFromDifference(
          amount,
          contract,
          month,
          firstDayAccountingOfYear,
          lastDayOfAccountingYearOfYear,
          avrgTimeCategory
        ),
        avrgTimeCategory,
      ]
    }

    default:
      return [null, null]
  }
}

function getValueFromStandardAndVacationBookings(
  amount: BookingTimeRelatedAmount,
  contract: Contract | undefined,
  month: Dayjs,
  mode: "standard" | "vacation",
  lastDayOfAccountingYear: Dayjs,
  avrgTimeCategory?: BookingTimeCategory | null
): number | null {
  const bookings = getBookings(contract, mode, lastDayOfAccountingYear)
  const validBooking = getValidTimeRangeItem(bookings, month)

  if (!validBooking) return null

  const {
    timeFactor: { category },
  } = validBooking

  const timeCategory = avrgTimeCategory ?? category
  if (!timeCategory) return null

  const value = getFeeAmount(amount, timeCategory)

  return value
}

function getValueFromDifference(
  amount: BookingTimeRelatedAmount,
  contract: Contract | undefined,
  month: Dayjs,
  firstDayAccounting: Dayjs,
  lastDayOfAccountingYear: Dayjs,
  avrgTimeCategoryForStandardBooking?: BookingTimeCategory | null
) {
  const bookings = contract?.bookings ?? []
  if (!bookings.length) return null

  const standardBookings = getBookings(contract, "standard", lastDayOfAccountingYear)
  const vacationBookings = getBookings(contract, "vacation", lastDayOfAccountingYear)

  const filteredVacationBookings = vacationBookings.filter(({ from, to }) =>
    isDateRangeOverlap(from, to, firstDayAccounting, lastDayOfAccountingYear)
  )

  //If there is no Ferienbuchung within the Betreuung year, then the calculation is equal to RegelBuchung
  const valueFromStandartBooking = getValueFromStandardAndVacationBookings(
    amount,
    contract,
    month,
    "standard",
    lastDayOfAccountingYear,
    avrgTimeCategoryForStandardBooking
  )
  const isThereAnyVacationBookingDay = filteredVacationBookings.some(
    ({ typeObject, bookingDays }) => {
      const { policy, days } = extractBookingDaysAndPolicy(typeObject)
      if (policy === BookingDaysEnum.ManuallySet) {
        return days
      }
      return bookingDays?.length
    }
  )

  if (!isThereAnyVacationBookingDay) return valueFromStandartBooking

  const { avrgTimeCategory, totalVacationMonths } = getAvrgTimeCategoryFromVacationBookings(
    filteredVacationBookings,
    firstDayAccounting,
    lastDayOfAccountingYear
  )
  const totalCareMonths = calculateMonthsOfCare(
    standardBookings,
    firstDayAccounting,
    lastDayOfAccountingYear
  )
  const valueFromVacationBooking = getFeeAmount(amount, avrgTimeCategory)

  if (valueFromStandartBooking === null) return null

  const difference =
    (((valueFromVacationBooking || 0) - (valueFromStandartBooking || 0)) / totalCareMonths) *
    totalVacationMonths

  return difference
}

function getFerienbuchungMonths(totalDays: number) {
  if (totalDays < 15) return 0
  if (totalDays < 30) return 1
  if (totalDays < 45) return 2
  return 3
}

function calculateMonthsOfCare(
  standardBookings: Booking[],
  firstDayAccounting: Dayjs,
  lastDayOfAccountingYear: Dayjs
) {
  let latest: Dayjs | null = null

  const filteredStandardBookings = standardBookings.filter(({ from, to }) =>
    isDateRangeOverlap(from, to, firstDayAccounting, lastDayOfAccountingYear)
  )
  if (!filteredStandardBookings.length) return 0

  const isAllStandardBookingsEndsBeforeCareYear = filteredStandardBookings.every(({ to }) =>
    dayjs(to).isBefore(lastDayOfAccountingYear)
  )
  if (isAllStandardBookingsEndsBeforeCareYear) {
    filteredStandardBookings.forEach(({ to }) => {
      const date = dayjs(to)

      if (date && (!latest || date.isAfter(latest))) {
        latest = date
      }
    })
  }

  const totalCareMonths = latest
    ? (latest as Dayjs).diff(firstDayAccounting, "month")
    : lastDayOfAccountingYear.diff(firstDayAccounting, "month")

  return totalCareMonths + 1
}

function getAvrgTimeCategoryFromVacationBookings(
  vacationBookings: Booking[],
  firstDayAccounting: Dayjs,
  lastDayOfAccountingYear: Dayjs
) {
  let totalDays = 0
  let totalHours = 0

  for (const booking of vacationBookings) {
    const {
      bookingDays,
      typeObject,

      timeFactor: { category: timeCategory },
    } = booking

    const timeFactor = bookingTimeCategoryToFeeTimeFactor(timeCategory)

    let daysCount = 0

    const { policy, days } = extractBookingDaysAndPolicy(typeObject)

    if (policy === BookingDaysEnum.ManuallySet) {
      daysCount = days ?? 0
    } else {
      const validBookingDays = (bookingDays ?? [])
        .map(({ date }) => date)
        .filter((date) =>
          dayjs(date).isBetween(firstDayAccounting, lastDayOfAccountingYear, "day", "[]")
        )
      daysCount = validBookingDays?.length ?? 0
    }
    const hours = daysCount * timeFactor

    totalDays += daysCount
    totalHours += hours
  }

  const avrgHours = totalDays ? totalHours / totalDays : 0
  const totalVacationMonths = getFerienbuchungMonths(totalDays)

  return { avrgTimeCategory: determineTimeCategory(avrgHours), totalVacationMonths }
}

function getValueFromYearEnd(
  amount: BookingTimeRelatedAmount,
  contract: Contract | undefined,
  month: Dayjs,
  feeType: FeeType,
  firstDayAccounting: Dayjs,
  lastDayOfAccountingYear: Dayjs,
  avrgTimeCategoryForStandardBooking?: BookingTimeCategory | null
) {
  const valueFromStandartBooking = getValueFromStandardAndVacationBookings(
    amount,
    contract,
    month,
    "standard",
    lastDayOfAccountingYear,
    avrgTimeCategoryForStandardBooking
  )
  //Can only be applied on fees where their basis fee type has Beitragstyp “Grundbeitrag”.
  if (feeType?.category !== FeeCategory.BaseFee) return valueFromStandartBooking

  const bookings = contract?.bookings ?? []
  if (!bookings.length) return null

  const vacationBookings = getBookings(contract, "vacation", lastDayOfAccountingYear)
  const filteredVacationBookings = vacationBookings.filter(({ from, to }) =>
    isDateRangeOverlap(from, to, firstDayAccounting, lastDayOfAccountingYear)
  )

  if (!filteredVacationBookings.length) return valueFromStandartBooking

  const { avrgTimeCategory, totalVacationMonths } = getAvrgTimeCategoryFromVacationBookings(
    filteredVacationBookings,
    firstDayAccounting,
    lastDayOfAccountingYear
  )

  if (!isValidEndMonth(contract, month, lastDayOfAccountingYear, totalVacationMonths))
    return valueFromStandartBooking

  const value = getFeeAmount(amount, avrgTimeCategory)
  return value
}

const isValidEndMonth = (
  contract: Contract | undefined,
  month: Dayjs,
  lastDayOfAccountingYear: Dayjs,
  totalVacationMonths: number
) => {
  if (!totalVacationMonths) return false

  const endMonth = getEndMonth(contract, lastDayOfAccountingYear)
  const isMonthInRange = Array.from({ length: totalVacationMonths }).some((_, i) =>
    month.isSame(endMonth.subtract(i, "month"), "month")
  )

  return isMonthInRange
}

export function getToDate(
  mode: "standard" | "vacation" | "alternative",
  contract: Contract | undefined,
  from: string,
  to: string | undefined,
  lastDayOfAccountingYear: Dayjs
) {
  if (to) return to

  const { endOfContract, bookings } = contract || {}
  let latestStandardToDate = null

  if (mode !== "standard") {
    const overlappingStandardBookings = (bookings ?? [])
      .filter((booking) => booking.type === BookingTypeEnum.Standard)
      .filter((booking) =>
        isDateRangeOverlap(from, to ?? null, booking.from, booking.to || endOfContract)
      )

    const latestBooking = getLatestBooking(overlappingStandardBookings)
    latestStandardToDate = latestBooking?.to
  }

  const dates = [
    latestStandardToDate && dayjs(latestStandardToDate),
    endOfContract && dayjs(endOfContract),
    lastDayOfAccountingYear,
  ].filter((date) => !!date)
  const earliestDate = dayjs.min(dates)

  return earliestDate ? earliestDate.format("YYYY-MM-DD") : null
}

export function getBookings(
  contract: Contract | undefined,
  mode: "standard" | "vacation" | "alternative",
  lastDayOfAccountingYear: Dayjs
) {
  const bookings = (contract?.bookings ?? [])
    .filter(({ type }) =>
      mode === "standard"
        ? type === BookingTypeEnum.Standard || type === BookingTypeEnum.ShortTime
        : mode === "alternative"
          ? type === BookingTypeEnum.Alternative_1 || type === BookingTypeEnum.Alternative_2
          : isVacationBooking(type)
    )
    .map((booking) => ({
      ...booking,
      to: getToDate(mode, contract, booking.from, booking?.to, lastDayOfAccountingYear),
    }))

  return bookings
}

export function getEndMonth(contract: Contract | undefined, lastDayOfAccountingYear: Dayjs): Dayjs {
  const { endOfContract } = contract ?? {}
  const standardBookings = (contract?.bookings ?? []).filter(
    ({ type }) => type === BookingTypeEnum.Standard
  )
  // Find latest standard booking
  const latestBooking = getLatestBooking(standardBookings)

  // Priority: latest standard booking end → contract end → accounting end
  if (latestBooking?.to) {
    const latestEnd = dayjs(latestBooking.to)
    return latestEnd.isBefore(lastDayOfAccountingYear) ? latestEnd : lastDayOfAccountingYear
  }

  if (endOfContract) {
    const contractEnd = dayjs(endOfContract)
    return contractEnd.isBefore(lastDayOfAccountingYear) ? contractEnd : lastDayOfAccountingYear
  }

  return lastDayOfAccountingYear
}

export function getLatestBooking(bookings: Booking[]) {
  let latestBooking: Booking | null = null
  for (const booking of bookings) {
    if (!booking.to) {
      latestBooking = booking
      break
    }

    if (!latestBooking || dayjs(booking.to).isAfter(dayjs(latestBooking.to))) {
      latestBooking = booking
    }
  }

  return latestBooking
}

export function getValidStandardOrShortBooking(contract: Contract, month: Dayjs) {
  const bookings =
    contract?.bookings?.filter(
      ({ type }) => type === BookingTypeEnum.Standard || type === BookingTypeEnum.ShortTime
    ) ?? []

  if (!bookings.length) return null

  return getValidTimeRangeItem(bookings, month)
}

export const hasParentalFeeSubsidyException = (
  contract: Contract,
  feeType: FeeType,
  month: Dayjs
): boolean => {
  if (!contract || !month || !feeType) return false

  const validBooking = getValidStandardOrShortBooking(contract, month)
  if (!validBooking) return false

  const bookingHasException = validBooking.exceptionForParentalFeeSubsidy
  const isFeeGrant = feeType.category === FeeCategory.FeeGrant

  return bookingHasException && isFeeGrant
}

const categoryTimeFactorMap: Record<BookingTimeCategory, number> = {
  [BookingTimeCategory.Zero]: 0,
  [BookingTimeCategory.One]: 2,
  [BookingTimeCategory.Two]: 3,
  [BookingTimeCategory.Three]: 4,
  [BookingTimeCategory.Four]: 5,
  [BookingTimeCategory.Five]: 6,
  [BookingTimeCategory.Six]: 7,
  [BookingTimeCategory.Seven]: 8,
  [BookingTimeCategory.Eight]: 9,
  [BookingTimeCategory.Nine]: 10,
  [BookingTimeCategory.Ten]: 11,
  [BookingTimeCategory.Eleven]: 12,
  [BookingTimeCategory.Twelve]: 13,
}

export function bookingTimeCategoryToFeeTimeFactor(category: BookingTimeCategory): number {
  return categoryTimeFactorMap[category] ?? null
}

export function feeTimeFactorToBookingCategory(factor: number | null): BookingTimeCategory | null {
  if (!factor) return null
  const category = Object.entries(categoryTimeFactorMap).find(([, value]) => value === factor)
  return category ? (category[0] as BookingTimeCategory) : null
}
